import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatbotService } from "gen/clients/llts";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import Fab from "@mui/material/Fab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { useTheme } from "@mui/material/styles";

interface Props {
  username?: string;
}

const ChatbotPopup: React.FC<Props> = ({ username }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  const { control } = useChatKit({
    startScreen: {
      greeting: t("components.chatbotPopup.greeting"),
      prompts: [
        {
          label: t("components.chatbotPopup.prompts.0.label"),
          prompt: t("components.chatbotPopup.prompts.0.prompt"),
          icon: "info"
        },
        {
          label: t("components.chatbotPopup.prompts.1.label"),
          prompt: t("components.chatbotPopup.prompts.1.prompt"),
          icon: "globe"
        },
        {
          label: t("components.chatbotPopup.prompts.2.label"),
          prompt: t("components.chatbotPopup.prompts.2.prompt"),
          icon: "sparkle"
        }
      ]
    },
    composer: {
      placeholder: t("components.chatbotPopup.composerPlaceholder")
    },
    threadItemActions: {
      feedback: false,
      retry: false
    },
    history: {
      enabled: !!username
    },
    api: {
      async getClientSecret(existing) {
        if (existing) {
          // implement session refresh
        }
        const res = username
          ? await ChatbotService.crateAuthenticatedChatbotSession()
          : await ChatbotService.crateUnauthenticatedChatbotSession();
        return res.sessionSecret;
      }
    }
  });

  const handleToggle = () => {
    setIsOpen(prev => !prev);
    setIsMaximized(false);
  };

  const handleMaximize = () => {
    setIsMaximized(prev => !prev);
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label={t("components.chatbotPopup.openButtonAriaLabel")}
        onClick={handleToggle}
        sx={{
          position: "fixed",
          bottom: 68,
          right: 24,
          zIndex: 1300,
          width: 64,
          height: 64,
          boxShadow: "0 4px 20px rgba(0, 83, 136, 0.4)",
          transition: "all 0.3s ease-in-out",
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || "#003d66"} 100%)`,
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: "0 6px 28px rgba(0, 83, 136, 0.5)"
          }
        }}
      >
        {isOpen ? <CloseIcon sx={{ fontSize: 28 }} /> : <QuestionMarkIcon sx={{ fontSize: 28 }} />}
      </Fab>

      {/* Chat Popup */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            bottom: isMaximized ? 0 : 140,
            right: isMaximized ? 0 : 24,
            top: isMaximized ? 0 : "auto",
            left: isMaximized ? 0 : "auto",
            zIndex: 1299,
            borderRadius: isMaximized ? 0 : 3,
            overflow: "hidden",
            boxShadow: isMaximized ? "0 0 0 rgba(0, 0, 0, 0)" : "0 8px 40px rgba(0, 0, 0, 0.2)",
            border: `2px solid ${theme.palette.primary.main}`,
            transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || "#003d66"} 100%)`,
              color: "white"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyIcon />
              <span style={{ fontWeight: 600, fontSize: "1rem" }}>{t("components.chatbotPopup.headerTitle")}</span>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" onClick={handleMaximize} sx={{ color: "white" }}>
                {isMaximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={handleToggle} sx={{ color: "white" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* ChatKit Container */}
          <Box
            sx={{
              height: isMaximized ? "calc(100vh - 60px)" : 500,
              width: isMaximized ? "100vw" : 380,
              backgroundColor: "#fff"
            }}
          >
            <ChatKit control={control} style={{ height: "100%", width: "100%" }} />
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export { ChatbotPopup };
